import { useState } from 'react';
import { Newspaper, Bookmark, Shield } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/utils/cn';

interface WelcomeModalProps {
  isOpen: boolean;
  onComplete: () => void;
}

const slides = [
  {
    icon: Newspaper,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    title: 'Your curated news feed',
    description:
      'AI-classified articles from 50+ tech sources. Must-read stories rise to the top. Your thumbs up and down train the feed over time.',
  },
  {
    icon: Bookmark,
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    title: 'Save what matters',
    description:
      'Save articles from the feed as links. Add notes with your thoughts. Build your personal knowledge base over time.',
  },
  {
    icon: Shield,
    iconBg: 'bg-brand-100',
    iconColor: 'text-brand-600',
    title: 'Private by design',
    description:
      'Your saved workspace — links, notes, ideas, files — lives in your Google Drive. We never see it. AI assistant is optional.',
  },
];

export function WelcomeModal({ isOpen, onComplete }: WelcomeModalProps) {
  const [currentSlide, setCurrentSlide] = useState(0);

  const isLastSlide = currentSlide === slides.length - 1;
  const slide = slides[currentSlide];
  if (!slide) return null;
  const Icon = slide.icon;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onComplete()}>
      <DialogContent
        className="max-w-md p-0"
        showCloseButton={false}
        aria-describedby={undefined}
        aria-label="Welcome to Personal Insights"
      >
        <DialogTitle className="sr-only">Welcome to Personal Insights</DialogTitle>
        <div className="p-8 text-center">
          {/* Icon */}
          <div
            className={cn(
              'w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6',
              slide.iconBg
            )}
          >
            <Icon className={cn('h-8 w-8', slide.iconColor)} />
          </div>

          {/* Content */}
          <h2 className="text-xl font-bold text-gray-900 mb-3">{slide.title}</h2>
          <p className="text-gray-500 text-sm leading-relaxed mb-8">{slide.description}</p>

          {/* Dot indicators */}
          <div className="flex justify-center gap-2 mb-6">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentSlide(i)}
                className={cn(
                  'w-2 h-2 rounded-full transition-colors',
                  i === currentSlide ? 'bg-brand-600' : 'bg-gray-200'
                )}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={onComplete}
              className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
              Skip
            </button>
            <Button
              onClick={() => {
                if (isLastSlide) {
                  onComplete();
                } else {
                  setCurrentSlide((s) => s + 1);
                }
              }}
            >
              {isLastSlide ? 'Get Started' : 'Next'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
